import { brand } from '@/brand/brand'

const TOKEN_KEY = 'aioverlords_auth_token'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

/** Shape of the demo-lock 403 payload from any AI/paid trigger (see app/core/demo_lock.py). */
export interface DemoLockInfo {
  feature?: string
  sampleUrl?: string | null
}

/** Detect the standard demo_locked 403 body: `{ detail: { code: "demo_locked", ... } }`. */
export function isDemoLocked(payload: any): boolean {
  const d = payload?.detail ?? payload
  return !!d && typeof d === 'object' && d.code === 'demo_locked'
}

/** Open the global <DemoLockOverlay> with the branded LockedStep + canned sample. */
export function showDemoLock(info: DemoLockInfo = {}): void {
  window.dispatchEvent(new CustomEvent('demo-lock', { detail: info }))
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getAuthToken()
  const headers = new Headers(options.headers || {})

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  if (!headers.has('Content-Type') && options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const fullUrl = url.startsWith('/api') && API_BASE_URL ? `${API_BASE_URL}${url}` : url
  const response = await fetch(fullUrl, { ...options, headers })

  // Demo lock: every AI/paid trigger returns a 403 demo_locked payload. Detect it here
  // (one place, every trigger) → open the branded <LockedStep> overlay with the canned
  // sample, and hand callers a clean on-brand 403 so no error path prints "[object Object]".
  if (response.status === 403) {
    const body = await response.clone().json().catch(() => null)
    if (isDemoLocked(body)) {
      showDemoLock({ feature: body.detail?.feature, sampleUrl: body.detail?.sample_url })
      return new Response(JSON.stringify({ detail: brand.copy.lockedTitle }), {
        status: 403,
        headers: { 'Content-Type': 'application/json' },
      })
    }
  }

  if (response.status === 401) {
    clearAuthToken()
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
  }

  return response
}

export async function safeJsonParse(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    const text = await response.text()
    throw new Error(text || `Request failed with status ${response.status}`)
  }
  return response.json()
}

export async function apiJson<T = unknown>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await apiFetch(url, options)
  const data = await safeJsonParse(response)
  if (!response.ok) {
    const detail = typeof data?.detail === 'string' ? data.detail : data?.detail?.detail || 'Request failed'
    throw new Error(detail)
  }
  return data as T
}
